import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button} from '@/shared/ui/button';
import {Label} from '@/shared/ui/label';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/command';
import {Popover, PopoverContent, PopoverTrigger} from '@/shared/ui/popover';
import {Check, ChevronsUpDown} from 'lucide-react';
import {cn} from '@/lib/utils';
import {
  getAllCountries,
  getCitiesForState,
  getStatesForCountry,
  resolveLocationCodes,
  type ICity,
  type ICountry,
  type IState,
} from '@/utils/locationData';
import {resolveTimezoneFromLocationCodes} from '@/utils/stateTimezone';

export interface CountryStateCityDropdownProps {
  country: string;
  state: string;
  city: string;
  onCountryChange: (country: string) => void;
  onStateChange: (state: string) => void;
  onCityChange: (city: string) => void;
  /** Called when location resolves a timezone (state, city, or country fallback). */
  onTimezoneFromState?: (timezone: string) => void;
  /** When true, auto-populate skips updates until country/state changes reset this ref. */
  timezoneUserOverrideRef?: React.MutableRefObject<boolean>;
  disabled?: boolean;
  className?: string;
  countryLabel?: string;
  stateLabel?: string;
  cityLabel?: string;
  countryRequired?: boolean;
  stateRequired?: boolean;
  cityRequired?: boolean;
}

type LocationOption = {value: string; label: string};

interface LocationComboboxProps {
  label: string;
  placeholder: string;
  value: string;
  options: LocationOption[];
  disabled: boolean;
  loading?: boolean;
  required?: boolean;
  onSelect: (value: string) => void;
}

function LocationCombobox({
  label,
  placeholder,
  value,
  options,
  disabled,
  loading,
  required,
  onSelect,
}: Readonly<LocationComboboxProps>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options.slice(0, 200);
    return options
      .filter((o) => o.label.toLowerCase().includes(q))
      .slice(0, 500);
  }, [options, search]);

  const displayLabel =
    options.find((o) => o.value === value)?.label || value || '';

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-normal text-foreground">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between bg-white font-normal"
            disabled={disabled || loading}
          >
            {loading ? (
              <span className="text-muted-foreground">Loading...</span>
            ) : displayLabel ? (
              <span className="truncate">{displayLabel}</span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] max-w-[400px] p-0"
          align="start"
          sideOffset={4}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <Command
            shouldFilter={false}
            className="max-h-[min(320px,50vh)] overflow-hidden"
          >
            <CommandInput
              placeholder={`Search ${label.toLowerCase()}...`}
              value={search}
              onValueChange={setSearch}
            />
            <CommandList className="max-h-[min(280px,45vh)] min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y">
              <CommandEmpty>
                {loading
                  ? 'Loading...'
                  : options.length === 0
                    ? 'Select a parent field first'
                    : 'No results found.'}
              </CommandEmpty>
              <CommandGroup className="overflow-visible">
                {filtered.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => {
                      onSelect(option.value);
                      setOpen(false);
                      setSearch('');
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        value === option.value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function CountryStateCityDropdown({
  country,
  state,
  city,
  onCountryChange,
  onStateChange,
  onCityChange,
  onTimezoneFromState,
  timezoneUserOverrideRef,
  disabled = false,
  className,
  countryLabel = 'Country',
  stateLabel = 'State',
  cityLabel = 'City',
  countryRequired = false,
  stateRequired = false,
  cityRequired = false,
}: Readonly<CountryStateCityDropdownProps>) {
  const [countryCode, setCountryCode] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [countries, setCountries] = useState<ICountry[]>([]);
  const [states, setStates] = useState<IState[]>([]);
  const [cities, setCities] = useState<ICity[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const lastAutoTimezoneKeyRef = useRef('');

  useEffect(() => {
    let mounted = true;
    setLoadingCountries(true);
    getAllCountries()
      .then((data) => {
        if (mounted) setCountries(data);
      })
      .finally(() => {
        if (mounted) setLoadingCountries(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!country && !state) {
      setCountryCode('');
      setStateCode('');
      return;
    }
    resolveLocationCodes(country, state).then((resolved) => {
      if (mounted) {
        setCountryCode(resolved.countryCode);
        setStateCode(resolved.stateCode);
      }
    });
    return () => {
      mounted = false;
    };
  }, [country, state]);

  useEffect(() => {
    let mounted = true;
    if (!countryCode) {
      setStates([]);
      return;
    }
    setLoadingStates(true);
    getStatesForCountry(countryCode)
      .then((data) => {
        if (mounted) setStates(data);
      })
      .finally(() => {
        if (mounted) setLoadingStates(false);
      });
    return () => {
      mounted = false;
    };
  }, [countryCode]);

  useEffect(() => {
    let mounted = true;
    if (!countryCode || !stateCode) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    getCitiesForState(countryCode, stateCode)
      .then((data) => {
        if (mounted) setCities(data);
      })
      .finally(() => {
        if (mounted) setLoadingCities(false);
      });
    return () => {
      mounted = false;
    };
  }, [countryCode, stateCode]);

  const applyTimezoneFromLocation = useCallback(
    (countryIso: string, stateIso: string, cityName?: string) => {
      if (!onTimezoneFromState || !countryIso) return;
      if (timezoneUserOverrideRef?.current) return;

      void resolveTimezoneFromLocationCodes(
        countryIso,
        stateIso,
        cityName?.trim() || undefined,
      ).then((tz) => {
        if (tz) onTimezoneFromState(tz);
      });
    },
    [onTimezoneFromState, timezoneUserOverrideRef],
  );

  // Auto-populate when country + state are set (city refines when present)
  useEffect(() => {
    if (!onTimezoneFromState || !countryCode) return;

    if (!stateCode) {
      if (loadingStates || states.length > 0) return;
      const key = `${countryCode}|country`;
      if (lastAutoTimezoneKeyRef.current === key) return;
      lastAutoTimezoneKeyRef.current = key;
      applyTimezoneFromLocation(countryCode, '');
      return;
    }

    const key = `${countryCode}|${stateCode}|${city}`;
    if (lastAutoTimezoneKeyRef.current === key) return;
    lastAutoTimezoneKeyRef.current = key;
    applyTimezoneFromLocation(countryCode, stateCode, city);
  }, [
    countryCode,
    stateCode,
    city,
    states.length,
    loadingStates,
    onTimezoneFromState,
    applyTimezoneFromLocation,
  ]);

  const countryOptions: LocationOption[] = useMemo(
    () =>
      countries.map((c) => ({
        value: c.isoCode,
        label: c.name,
      })),
    [countries],
  );

  const stateOptions: LocationOption[] = useMemo(
    () =>
      states.map((s) => ({
        value: s.isoCode,
        label: s.name,
      })),
    [states],
  );

  const cityOptions: LocationOption[] = useMemo(
    () =>
      cities.map((c) => ({
        value: c.name,
        label: c.name,
      })),
    [cities],
  );

  const handleCountrySelect = useCallback(
    (isoCode: string) => {
      const selected = countries.find((c) => c.isoCode === isoCode);
      onCountryChange(selected?.name ?? '');
      onStateChange('');
      onCityChange('');
      setCountryCode(isoCode);
      setStateCode('');
      lastAutoTimezoneKeyRef.current = '';
      if (timezoneUserOverrideRef) timezoneUserOverrideRef.current = false;
      onTimezoneFromState?.('');
    },
    [
      countries,
      onCountryChange,
      onStateChange,
      onCityChange,
      onTimezoneFromState,
      timezoneUserOverrideRef,
    ],
  );

  const handleStateSelect = useCallback(
    (isoCode: string) => {
      const selected = states.find((s) => s.isoCode === isoCode);
      const countryIso = selected?.countryCode ?? countryCode;
      onStateChange(selected?.name ?? '');
      onCityChange('');
      setStateCode(isoCode);
      setCountryCode(countryIso);
      lastAutoTimezoneKeyRef.current = '';
      if (timezoneUserOverrideRef) timezoneUserOverrideRef.current = false;
      applyTimezoneFromLocation(countryIso, isoCode);
    },
    [
      states,
      countryCode,
      onStateChange,
      onCityChange,
      applyTimezoneFromLocation,
      timezoneUserOverrideRef,
    ],
  );

  const handleCitySelect = useCallback(
    (cityName: string) => {
      onCityChange(cityName);
      if (countryCode && stateCode) {
        lastAutoTimezoneKeyRef.current = '';
        if (timezoneUserOverrideRef) timezoneUserOverrideRef.current = false;
        applyTimezoneFromLocation(countryCode, stateCode, cityName);
      }
    },
    [
      onCityChange,
      countryCode,
      stateCode,
      applyTimezoneFromLocation,
      timezoneUserOverrideRef,
    ],
  );

  const stateDisabled = disabled || !countryCode;
  const cityDisabled = disabled || !countryCode || !stateCode;

  return (
    <div className={cn('space-y-4', className)}>
      <LocationCombobox
        label={countryLabel}
        placeholder="Select country..."
        value={countryCode}
        options={countryOptions}
        disabled={disabled}
        loading={loadingCountries}
        required={countryRequired}
        onSelect={handleCountrySelect}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LocationCombobox
          label={stateLabel}
          placeholder={
            countryCode ? 'Select state...' : 'Select country first'
          }
          value={stateCode}
          options={stateOptions}
          disabled={stateDisabled}
          loading={loadingStates}
          required={stateRequired}
          onSelect={handleStateSelect}
        />
        <LocationCombobox
          label={cityLabel}
          placeholder={
            stateCode ? 'Select city...' : 'Select state first'
          }
          value={city}
          options={cityOptions}
          disabled={cityDisabled}
          loading={loadingCities}
          required={cityRequired}
          onSelect={handleCitySelect}
        />
      </div>
    </div>
  );
}
